import { useState, useEffect } from 'react';
import coursesData from '../courses.json';

/**
 * Custom hook for loading and processing course data
 * Handles the complex logic of processing course instances, spans, and grouping
 * @returns {Object} - { groupedCourses, courseOrders, setGroupedCourses, setCourseOrders }
 */
export const useCourseData = () => {
  const [groupedCourses, setGroupedCourses] = useState({});
  const [courseOrders, setCourseOrders] = useState({});

  useEffect(() => {
    const grouped = {};

    coursesData.courses.forEach((course) => {
      // For split courses spanning multiple instances, calculate total periods and chronological index
      const hasAnyPeriodCredits = course.instances.some(instance => 
        (instance.span && instance.span.some(spanEntry => spanEntry.period_credits && spanEntry.period_credits.length > 0)) ||
        (instance.period_credits && instance.period_credits.length > 0)
      );
      
      let totalPeriods = 0;
      let chronologicalPeriods = [];
      
      if (hasAnyPeriodCredits) {
        // Sort instances chronologically and build a list of all periods
        const sortedInstances = [...course.instances].sort((a, b) => {
          if (a.span && b.span) {
            const aFirst = a.span[0];
            const bFirst = b.span[0];
            if (aFirst.year !== bFirst.year) return aFirst.year - bFirst.year;
            return aFirst.semester - bFirst.semester;
          }
          if (a.span) {
            const aFirst = a.span[0];
            if (aFirst.year !== b.year) return aFirst.year - b.year;
            return aFirst.semester - b.semester;
          }
          if (b.span) {
            const bFirst = b.span[0];
            if (a.year !== bFirst.year) return a.year - bFirst.year;
            return a.semester - bFirst.semester;
          }
          if (a.year !== b.year) return a.year - b.year;
          return a.semester - b.semester;
        });
        
        sortedInstances.forEach((instance) => {
          if (instance.span) {
            // Handle span courses
            const sortedSpan = [...instance.span].sort((a, b) => {
              if (a.year !== b.year) return a.year - b.year;
              return a.semester - b.semester;
            });
            
            sortedSpan.forEach((spanEntry) => {
              if (spanEntry.period_credits && spanEntry.period_credits.length > 0) {
                spanEntry.periods.forEach((period, periodIndex) => {
                  chronologicalPeriods.push({
                    year: spanEntry.year,
                    semester: spanEntry.semester,
                    period: period,
                    credits: spanEntry.period_credits[periodIndex],
                    chronological_index: totalPeriods
                  });
                  totalPeriods++;
                });
              }
            });
          } else if (instance.period_credits && instance.period_credits.length > 0) {
            instance.periods.forEach((period, periodIndex) => {
              chronologicalPeriods.push({
                year: instance.year,
                semester: instance.semester,
                period: period,
                credits: instance.period_credits[periodIndex],
                chronological_index: totalPeriods
              });
              totalPeriods++;
            });
          }
        });
      }

      // Process each instance of the course
      course.instances.forEach((instance, instanceIndex) => {
        // Check if this instance spans multiple semesters/years
        if (instance.span) {
          // Handle courses that span multiple semesters/years
          let overallPeriodIndex = 0;
          const allPeriodCredits = [];
          
          // Collect all period credits across the span
          instance.span.forEach(spanEntry => {
            if (spanEntry.period_credits) {
              allPeriodCredits.push(...spanEntry.period_credits);
            }
          });
          
          instance.span.forEach((spanEntry) => {
            const { year, semester, periods, period_credits } = spanEntry;
            
            if (period_credits && period_credits.length > 0) {
              periods.forEach((period, localPeriodIndex) => {
                const groupKey = `${year}_s${semester}_p${period}`;
                
                if (!grouped[groupKey]) {
                  grouped[groupKey] = {
                    year,
                    semester,
                    period,
                    courses: [],
                  };
                }

                // Create a course entry with period-specific credits and shared instance ID
                const courseEntry = {
                  ...course,
                  credits: period_credits[localPeriodIndex],
                  original_credits: course.credits,
                  period_index: overallPeriodIndex, // Index across the entire span
                  actual_period: period,
                  is_split_course: true,
                  period_credits: allPeriodCredits, // All period credits across the span
                  total_periods: totalPeriods,
                  semester_instance: `${course.course_code}_instance${instanceIndex}` // Shared across span
                };

                grouped[groupKey].courses.push(courseEntry);
                overallPeriodIndex++;
              });
            }
          });
        } else {
          // Handle regular instances (existing logic)
          const { year, semester, periods, period_credits } = instance;
          
          // Handle courses with period_credits (spanning multiple periods)
          if (period_credits && period_credits.length > 0) {
            periods.forEach((period, periodIndex) => {
              const groupKey = `${year}_s${semester}_p${period}`;
              
              if (!grouped[groupKey]) {
                grouped[groupKey] = {
                  year,
                  semester,
                  period,
                  courses: [],
                };
              }

              // For split courses, use the period index within this instance
              // Create a course entry with period-specific credits and unique semester ID
              const courseEntry = {
                ...course,
                credits: period_credits[periodIndex],
                original_credits: course.credits,
                period_index: periodIndex, // Index within this specific instance
                actual_period: period, // Store the actual period number
                is_split_course: true,
                period_credits: period_credits, // Include the period_credits array
                total_periods: totalPeriods, // Total periods across all instances
                semester_instance: `${course.course_code}_y${year}_s${semester}` // Unique per semester
              };

              grouped[groupKey].courses.push(courseEntry);
            });
          } else {
            // Handle single-credit courses (no period_credits)
            periods.forEach((period) => {
              const groupKey = `${year}_s${semester}_p${period}`;

              if (!grouped[groupKey]) {
                grouped[groupKey] = {
                  year,
                  semester,
                  period,
                  courses: [],
                };
              }

              // Create a course entry with unique semester ID
              const courseEntry = {
                ...course,
                is_split_course: false,
                semester_instance: `${course.course_code}_y${year}_s${semester}` // Unique per semester
              };

              grouped[groupKey].courses.push(courseEntry);
            });
          }
        }
      });
    });

    // Initialize course orders with default order
    const initialOrders = {};
    Object.keys(grouped).forEach(groupKey => {
      const courseIds = grouped[groupKey].courses.map(course => 
        course.is_split_course 
          ? `${course.semester_instance}_p${course.actual_period}`
          : course.semester_instance
      );
      initialOrders[groupKey] = courseIds;
    });

    setGroupedCourses(grouped);
    setCourseOrders(initialOrders);
  }, []);

  return {
    groupedCourses,
    courseOrders,
    setGroupedCourses,
    setCourseOrders
  };
}; 